import { computed, type ComputedRef } from 'vue'
import { useLocalStorage } from '@vueuse/core'

/**
 * Deterministic per-visitor PWA feature flag.
 *
 * Algorytm:
 *  1) `pwaEnabled === false` → zwraca zawsze `false` (kill switch).
 *  2) `pwaRolloutPercent >= 100` → `true`; `<= 0` → `false`.
 *  3) W przeciwnym razie liczy bucket 0-99 z `visitorId`
 *     (UUID v4 z localStorage, persystowane). Ten sam visitor
 *     zawsze trafia w ten sam bucket → deterministyczny rollout.
 */
export function usePwaFeatureFlag(): ComputedRef<boolean> {
  const config = useRuntimeConfig()

  if (!config.public.pwaEnabled) {
    return computed(() => false)
  }

  const visitorId = useLocalStorage<string>('lavmag.visitorId', () => {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID()
    }
    return Math.random().toString(36).slice(2) + Date.now().toString(36)
  })

  return computed(() => {
    const percent = config.public.pwaRolloutPercent
    if (percent >= 100) return true
    if (percent <= 0) return false

    // Deterministyczny bucket z visitorId (cyfry 0-9 z UUID-a → mod 100)
    const digits = visitorId.value.replace(/\D/g, '')
    const numeric = digits.length >= 8 ? parseInt(digits.slice(0, 8), 10) : 0
    const bucket = numeric % 100
    return bucket < percent
  })
}
